import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme, fade } from '@material-ui/core/styles';
import {
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Delete as DeleteIcon,
  InsertDriveFile as GenericFileIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  GetApp as DownloadIcon,
  Home as HomeIcon,
  PhotoLibrary as ImageIcon,
  MoreVert as MoreVertIcon,
  PictureAsPdf as PdfIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  Block as BlockIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  ChevronRight as ChevronRightIcon,
  ErrorOutline as ErrorOutlineIcon,
  NoteAdd as NoteAddIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  ArrowForwardIos as ArrowIcon,
  Event as EventIcon,
  Unarchive as UnarchiveIcon,
} from '@material-ui/icons';
import {
  archiviaDocumento,
  getDocumentiPerEsercizio,
  getDocumentoDettaglio,
  uploadDocumentoEsercizio,
  updateDocumentoEsercizio,
  deleteDocumentoEsercizio,
  getDocumentoFile,
  createCartella,
  updateCartella,
  deleteCartella,
  getCartelleAnnidate,
  getRepartiVisibilita,
  duplicateTitleConflict,
  DUPLICATE_TITLE_MESSAGE,
} from '../../../../utils/api/documenti_api';
import CustomSnackbar from '../../../../components/CustomSnackbar';
import WarningBanner from '../../../../components/WarningBanner';
import FolderDialog from './FolderDialog';
import OverwriteAttachmentDialog from './OverwriteAttachmentDialog';
import VisibilitaField from './VisibilitaField';
import VisibilitaPills from './VisibilitaPills';
import ArchiviaDocumentoDialog from './ArchiviaDocumentoDialog';
import ArchiviatoBadge from './ArchiviatoBadge';
import PromemoriaScadenzeButton from '../promemoria_scadenze/PromemoriaScadenzeButton';
import RiepilogoPromemoria from '../promemoria_scadenze/RiepilogoPromemoria';
import usePromemoriaScadenze from '../promemoria_scadenze/usePromemoriaScadenze';
import PresetScadenzaChips from './PresetScadenzaChips';
import ScadenzaPill from './ScadenzaPill';
import VistaDocumentiToggle from './VistaDocumentiToggle';
import { descriviPercorso, trovaPreset } from './scadenzaDocumento';
import { formattaGiornoIso } from '../iot/formatoData';
import { isFolderNameValid } from './folderNameValidation';
import {
  INTERA_STRUTTURA,
  idsVisibilita,
  isVisibilitaAmmessa,
  isVisibilitaCompleta,
  restrizioneEreditata,
  visibilitaDa,
} from './visibilitaAllegati';
import { reportError } from '../../../../utils/reportApiError';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(4, 5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3),
    },
  },

  /* ─── Header / Breadcrumbs ─── */
  headerSection: {
    marginBottom: theme.spacing(5),
  },
  breadcrumbs: {
    '& .MuiBreadcrumbs-separator': {
      color: theme.palette.text.secondary,
    },
  },
  breadcrumbLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 8px',
    borderRadius: 6,
    fontWeight: 500,
    fontSize: '0.875rem',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: fade(theme.palette.primary.main, 0.08),
      textDecoration: 'none',
    },
  },
  breadcrumbCurrent: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  breadcrumbText: {
    display: 'inline-block',
    maxWidth: 260,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 120,
    },
  },
  pageTitle: {
    marginTop: theme.spacing(2),
    fontWeight: 700,
    fontSize: '1.5rem',
    letterSpacing: '-0.02em',
    color: theme.palette.text.primary,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pageSubtitle: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },

  /* ─── Toolbar ─── */
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(4),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 200,
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  searchField: {
    minWidth: 280,
    maxWidth: 360,
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.controlRadius,
      backgroundColor: theme.palette.background.paper,
      transition: 'box-shadow 0.2s ease',
      '&:hover': {
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      },
      '&.Mui-focused': {
        boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.15)}`,
      },
    },
  },
  sortButton: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.controlRadius,
    padding: theme.spacing(1.5),
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: fade(theme.palette.primary.main, 0.06),
      borderColor: theme.palette.primary.main,
    },
  },
  sortButtonActive: {
    backgroundColor: fade(theme.palette.primary.main, 0.08),
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
  actionButton: {
    borderRadius: theme.shape.controlRadius,
    padding: '8px 20px',
    fontWeight: 600,
    fontSize: '0.8125rem',
    textTransform: 'none',
    letterSpacing: '0.01em',
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      transform: 'translateY(-1px)',
    },
  },
  outlinedAction: {
    borderWidth: '1.5px',
    '&:hover': {
      borderWidth: '1.5px',
    },
  },
  primaryAction: {
    '&:hover': {
      boxShadow: `0 4px 16px ${fade(theme.palette.primary.main, 0.35)}`,
    },
  },

  /* ─── Table ─── */
  tableWrapper: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  tableHead: {
    backgroundColor: '#FAFAFA',
  },
  tableHeadCell: {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: '12px 16px',
  },
  tableRow: {
    cursor: 'default',
    transition: 'background-color 0.15s ease',
    '&:last-child td': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: '#F8F9FA',
    },
  },
  folderRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: fade(theme.palette.primary.main, 0.04),
    },
  },
  tableCell: {
    padding: '10px 16px',
    borderBottom: `1px solid #F0F0F0`,
    fontSize: '0.875rem',
  },
  iconCell: {
    width: 56,
    padding: '10px 8px 10px 16px',
    borderBottom: `1px solid #F0F0F0`,
  },
  titleCell: {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  titleText: {
    display: 'inline-block',
    maxWidth: 480,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    [theme.breakpoints.down('md')]: {
      maxWidth: 320,
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: 180,
    },
  },
  folderTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  dateCell: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  actionsCell: {
    width: 48,
    padding: '10px 12px 10px 4px',
    borderBottom: `1px solid #F0F0F0`,
  },
  // «Ripristina» is written out, so its column fits the button, not an icon.
  restoreCell: {
    width: 1,
    whiteSpace: 'nowrap',
  },
  restoreButton: {
    borderRadius: theme.shape.controlRadius,
    fontWeight: 600,
    textTransform: 'none',
  },
  moreButton: {
    opacity: 0.4,
    transition: 'opacity 0.15s ease',
    '$tableRow:hover &, $folderRow:hover &': {
      opacity: 1,
    },
  },
  folderRowDragOver: {
    backgroundColor: `${fade(theme.palette.primary.main, 0.08)} !important`,
    outline: `2px dashed ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  draggableRow: {
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  folderArrow: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
  },

  /* ─── Empty state ─── */
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(10, 4),
    textAlign: 'center',
  },
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fade(theme.palette.primary.main, 0.08),
    marginBottom: theme.spacing(3),
  },
  emptyTitle: {
    fontWeight: 600,
    fontSize: '1.05rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  emptySubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    maxWidth: 320,
    lineHeight: 1.6,
  },
  emptyAction: {
    marginTop: theme.spacing(3),
  },

  /* ─── Loading ─── */
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(10),
    gap: theme.spacing(2),
  },

  /* ─── Dialogs ─── */
  dialogPaper: {
    borderRadius: theme.shape.dialogRadius,
    overflow: 'hidden',
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: '20px 24px 12px',
  },
  dialogTitleIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  dialogTitleText: {
    ...theme.typography.dialogTitle,
    letterSpacing: '-0.01em',
  },
  dialogContent: {
    padding: '8px 24px 16px',
  },
  dialogForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  dialogActions: {
    padding: '12px 24px 20px',
    gap: theme.spacing(2),
  },
  dialogInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.controlRadius,
    },
  },
  avvisoTitoloHelper: {
    marginLeft: 0,
    marginRight: 0,
    marginTop: theme.spacing(1),
  },
  dialogCancelBtn: {
    borderRadius: theme.shape.controlRadius,
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 20px',
    color: theme.palette.text.secondary,
  },
  dialogSaveBtn: {
    borderRadius: theme.shape.controlRadius,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 24px',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: `0 4px 12px ${fade(theme.palette.primary.main, 0.3)}`,
    },
  },
  dialogDeleteBtn: {
    borderRadius: theme.shape.controlRadius,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 24px',
    boxShadow: 'none',
    backgroundColor: '#EF4444',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#DC2626',
      boxShadow: '0 4px 12px rgba(239,68,68,0.35)',
    },
  },

  /* ─── File drop zone ─── */
  fileDropZone: {
    border: `2px dashed #D1D5DB`,
    borderRadius: 12,
    padding: theme.spacing(5),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#FAFAFA',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: fade(theme.palette.primary.main, 0.03),
    },
  },
  fileDropZoneActive: {
    borderColor: theme.palette.primary.main,
    borderStyle: 'solid',
    backgroundColor: fade(theme.palette.primary.main, 0.06),
  },
  dropZoneIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fade(theme.palette.primary.main, 0.1),
    marginBottom: theme.spacing(2),
  },
  selectedFileChip: {
    marginTop: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: fade(theme.palette.primary.main, 0.08),
    padding: '4px 8px',
  },

  /* ─── Folder picker (in document dialog) ─── */
  folderPickerSection: {
    marginTop: theme.spacing(1),
  },
  folderPickerLabel: {
    fontWeight: 600,
    fontSize: '0.8125rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  folderPickerContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    maxHeight: 200,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#D1D5DB',
      borderRadius: 3,
    },
  },
  folderPickerItem: {
    padding: '6px 12px',
    borderRadius: 0,
    transition: 'background-color 0.12s ease',
    '&:hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  folderPickerItemSelected: {
    backgroundColor: `${fade(theme.palette.primary.main, 0.1)} !important`,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },

  /* ─── Context menu ─── */
  contextMenu: {
    '& .MuiPaper-root': {
      borderRadius: 12,
      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
      border: '1px solid #F0F0F0',
      minWidth: 200,
    },
  },
  contextMenuItem: {
    borderRadius: 8,
    margin: '2px 6px',
    padding: '8px 12px',
    transition: 'background-color 0.12s ease',
    '&:hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  contextMenuDanger: {
    '&:hover': {
      backgroundColor: '#FEF2F2',
    },
  },

  /* ─── Sort menu ─── */
  sortMenu: {
    '& .MuiPaper-root': {
      borderRadius: 12,
      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
      border: '1px solid #F0F0F0',
      minWidth: 200,
    },
  },
  sortMenuItem: {
    borderRadius: 8,
    margin: '2px 6px',
    padding: '8px 14px',
    fontSize: '0.875rem',
    transition: 'background-color 0.12s ease',
  },
  sortMenuItemActive: {
    backgroundColor: fade(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },

  /* ─── Delete dialog ─── */
  deleteDialogIcon: {
    width: 56,
    height: 56,
    borderRadius: theme.shape.iconTileRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FEF2F2',
    margin: '0 auto',
    marginBottom: theme.spacing(3),
  },

  /* ─── Icon badge ─── */
  iconBadge: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'transform 0.15s ease',
    '$folderRow:hover &': {
      transform: 'scale(1.05)',
    },
  },

  /* ─── Stats bar ─── */
  statsBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    backgroundColor: '#FAFAFA',
    borderRadius: 12,
    border: `1px solid #F0F0F0`,
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  statIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statValue: {
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: 1,
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1,
  },
}));

const iconColors = {
  folder: { bg: '#FFF3E0', fg: '#F57C00' },
  pdf: { bg: '#FFEBEE', fg: '#D32F2F' },
  image: { bg: '#E3F2FD', fg: '#1976D2' },
  generic: { bg: '#ECEFF1', fg: '#607D8B' },
};

export const ALLEGATO_TITOLO_MAX_LENGTH = 100;

// The two forms when closed. One definition each: opening and closing used to
// spell the whole shape out again, and a field added to one copy was missing
// from the others.
const FOLDER_DIALOG_CHIUSO = {
  open: false,
  mode: 'create',
  titolo: '',
  titoloIniziale: '',
  cartellaId: null,
  cartellaSuperioreId: null,
  visibilita: INTERA_STRUTTURA,
  visibilitaIniziale: INTERA_STRUTTURA,
  repartiAmmessi: null,
};

// `visibilitaToccata` says whether the visibility shown is the operator's
// choice or the default taken from the destination folder: only the default
// follows the folder when another one is picked.
const DOCUMENT_DIALOG_CHIUSO = {
  open: false,
  mode: 'create',
  titolo: '',
  note: '',
  file: null,
  documentoId: null,
  dataScadenza: '',
  cartellaId: null,
  cartellaNome: null,
  visibilita: INTERA_STRUTTURA,
  visibilitaToccata: false,
};

const ARCHIVIA_DIALOG_CHIUSO = { open: false, item: null };

// The orders the server takes as `sort`, as the menu lists them; `breve` is the
// tooltip of the sort button. `data,DESC` is the order the list opens on.
const ORDINAMENTI = [
  { label: 'Più recenti', breve: 'Più recenti', field: 'data', dir: 'DESC' },
  { label: 'Meno recenti', breve: 'Meno recenti', field: 'data', dir: 'ASC' },
  { label: 'Titolo (A–Z)', breve: 'A–Z', field: 'titolo', dir: 'ASC' },
  { label: 'Titolo (Z–A)', breve: 'Z–A', field: 'titolo', dir: 'DESC' },
  {
    label: 'Scadenza (crescente)',
    breve: 'Scadenza crescente',
    field: 'scadenza',
    dir: 'ASC',
  },
  {
    label: 'Scadenza (decrescente)',
    breve: 'Scadenza decrescente',
    field: 'scadenza',
    dir: 'DESC',
  },
];

const FileIconBadge = ({ bg, fg, Icon, classes }) => (
  <span className={classes} style={{ backgroundColor: bg }}>
    <Icon style={{ fontSize: 20, color: fg }} />
  </span>
);

const getFileIcon = (item, badgeClass) => {
  if (item.tipo === 'CARTELLA') {
    return (
      <FileIconBadge
        bg={iconColors.folder.bg}
        fg={iconColors.folder.fg}
        Icon={FolderIcon}
        classes={badgeClass}
      />
    );
  }
  if (item.isPdf || item.pdf) {
    return (
      <FileIconBadge
        bg={iconColors.pdf.bg}
        fg={iconColors.pdf.fg}
        Icon={PdfIcon}
        classes={badgeClass}
      />
    );
  }
  const title = (item.titolo || '').toLowerCase();
  if (/\.(jpe?g|png|gif|webp)$/.test(title)) {
    return (
      <FileIconBadge
        bg={iconColors.image.bg}
        fg={iconColors.image.fg}
        Icon={ImageIcon}
        classes={badgeClass}
      />
    );
  }
  return (
    <FileIconBadge
      bg={iconColors.generic.bg}
      fg={iconColors.generic.fg}
      Icon={GenericFileIcon}
      classes={badgeClass}
    />
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

export default function DocumentiSection({ esercizioId }) {
  const classes = useStyles();
  const theme = useTheme();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const MAX_FOLDER_DEPTH = 2;
  const [folderStack, setFolderStack] = useState([]);
  const currentFolder =
    folderStack.length > 0 ? folderStack[folderStack.length - 1] : null;
  const canCreateSubfolder = folderStack.length < MAX_FOLDER_DEPTH;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimer = useRef(null);

  // `documenti` lists the active documents, `archivio` the archived ones; both
  // walk the same folders, and only the first one creates anything.
  const [vista, setVista] = useState('documenti');
  const inArchivio = vista === 'archivio';

  // The expiry preset, Allegati only. With one the list is flat: documents
  // from the whole tree, no folder rows.
  const [presetScadenza, setPresetScadenza] = useState(null);

  const [sortField, setSortField] = useState('data');
  const [sortDir, setSortDir] = useState('DESC');
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  // Who put the order on screen: `predefinito` is the one the list opens on,
  // `preset` the one a preset set, `operatore` an entry picked from the menu
  // that changed the order, the default one included. A preset changes the
  // order only when the operator has not picked one, and taking it off undoes
  // only its own.
  const [origineOrdine, setOrigineOrdine] = useState('predefinito');

  // Each read of the list takes a number, and only the answer to the latest one
  // lands: the view toggle, the chips and the search change the list faster
  // than the server answers, and an older answer arriving last would show rows
  // for filters no longer on screen.
  const ultimaLettura = useRef(0);
  // Whether the latest read failed. Its rows are not kept on screen: they were
  // read for another view, folder or preset, and the toggle and the chips would
  // describe them as this one's.
  const [letturaFallita, setLetturaFallita] = useState(false);
  // A write reads the list again by bumping this, never by calling `fetchData`
  // itself: its callbacks belong to the render where it started, whose
  // `fetchData` still carries the view, preset and folder of that moment, and
  // being the latest read it would land over the list of the filters on screen.
  const [rilettura, setRilettura] = useState(0);
  const rileggi = () => setRilettura((n) => n + 1);

  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [folderDialog, setFolderDialog] = useState(FOLDER_DIALOG_CHIUSO);
  const [documentDialog, setDocumentDialog] = useState(DOCUMENT_DIALOG_CHIUSO);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    item: null,
  });
  const [archiviaDialog, setArchiviaDialog] = useState(ARCHIVIA_DIALOG_CHIUSO);
  const [saving, setSaving] = useState(false);

  // The refusal for a title already taken, in two pieces with different lives:
  // the confirmation goes as soon as the operator chooses, the warning under the
  // Titolo field stays until the title changes, so dismissing the confirmation
  // does not take the reason with it.
  //
  // `titleConflict.pending` says which write was refused — `{ kind: 'form' }` for
  // the attachment dialog, `{ kind: 'move', item, cartellaId }` for a drag onto a
  // folder — because confirming has to repeat that same write, and the two are
  // not the same request.
  //
  // `takenTitle` is the title the server refused, not a flag: the warning is
  // about that title, so it belongs on screen for exactly as long as the field
  // still holds it. Trimmed, the way it is sent, so adding a space does not read
  // as a different title.
  const [titleConflict, setTitleConflict] = useState(null);
  const [takenTitle, setTakenTitle] = useState(null);

  // Said out loud rather than swallowed: a drop that lands while a write is still
  // in the air is refused, and a drag that appears to do nothing reads as a bug.
  // `seq` counts the notices: repeating the same sentence leaves MUI's own
  // auto-hide timer running from the first one, so the second drop would flash a
  // warning that is already most of the way through its four seconds.
  // `severity` is `success` for a write that went through, `info` otherwise.
  const [avviso, setAvviso] = useState({
    testo: '',
    seq: 0,
    severity: 'info',
  });
  const avvisa = (testo, severity = 'info') =>
    setAvviso((prev) => ({ testo, severity, seq: prev.seq + 1 }));

  // The expiry reminders of the esercizio (BSS-1968): the toolbar button and
  // the summary line under it read the same configuration.
  const promemoria = usePromemoriaScadenze(esercizioId);

  const [folderTree, setFolderTree] = useState([]);
  const [folderTreeLoading, setFolderTreeLoading] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const draggedItemRef = useRef(null);

  // The reparti an element can be restricted to, with the state of the read:
  // the pills tell a missing reparto from a present one only against a list
  // that was actually read, and the form says «could not read» rather than
  // «there are none» when it was not.
  const [reparti, setReparti] = useState({ stato: 'caricamento', lista: [] });

  // Read once for the list, and again whenever a form opens: this section stays
  // mounted while the operator publishes a gestione operativa in another tab,
  // and the form should offer the reparti of that publication. A list already
  // read stays on screen through the refresh — and through a refresh that
  // fails — so the form never goes blank over a list it had a moment ago.
  const fetchReparti = useCallback(() => {
    setReparti((prev) =>
      prev.stato === 'pronto' ? prev : { stato: 'caricamento', lista: [] },
    );
    getRepartiVisibilita(esercizioId)
      .then((lista) => {
        setReparti({
          stato: 'pronto',
          lista: Array.isArray(lista) ? lista : [],
        });
      })
      .catch(() => {
        setReparti((prev) =>
          prev.stato === 'pronto' ? prev : { stato: 'errore', lista: [] },
        );
      });
  }, [esercizioId]);

  useEffect(() => {
    fetchReparti();
  }, [fetchReparti]);

  const fetchFolderTree = useCallback(() => {
    setFolderTreeLoading(true);
    getCartelleAnnidate(esercizioId)
      .then((result) => {
        setFolderTree(Array.isArray(result) ? result : []);
        setFolderTreeLoading(false);
      })
      .catch(() => {
        setFolderTree([]);
        setFolderTreeLoading(false);
      });
  }, [esercizioId]);

  // Each folder with its depth, its own restriction and `restrizione`, the one
  // it passes down to what is created inside it: its own, else the nearest
  // restricted ancestor's. `catena` is the path from the root to `nodes`.
  const flattenFolderTree = useCallback((nodes, level = 0, catena = []) => {
    const result = [];
    for (const node of nodes) {
      const catenaNodo = [...catena, node];
      result.push({
        id: node.id,
        nome: node.nome,
        level,
        cartellaSuperioreId: node.cartellaSuperioreId ?? null,
        repartiVisibilita: node.repartiVisibilita || [],
        restrizione: restrizioneEreditata(catenaNodo),
      });
      if (node.sottocartelle && node.sottocartelle.length > 0) {
        result.push(
          ...flattenFolderTree(node.sottocartelle, level + 1, catenaNodo),
        );
      }
    }
    return result;
  }, []);

  // A search and a preset both look across the whole tree, so neither sends the
  // folder open on screen.
  const fetchData = useCallback(() => {
    const lettura = ++ultimaLettura.current;
    setLoading(true);
    const sort = `${sortField},${sortDir}`;
    getDocumentiPerEsercizio(esercizioId, {
      cartella:
        debouncedSearch || presetScadenza ? undefined : currentFolder?.id,
      sort,
      titolo: debouncedSearch || undefined,
      archivio: inArchivio ? 'ARCHIVIATI' : 'ATTIVI',
      scadenza: presetScadenza || undefined,
    })
      .then((result) => {
        if (lettura !== ultimaLettura.current) return;
        setData(Array.isArray(result) ? result : []);
        setLetturaFallita(false);
        setLoading(false);
      })
      .catch((err) => {
        if (lettura !== ultimaLettura.current) return;
        setData([]);
        setLetturaFallita(true);
        setLoading(false);
        reportError(err.message || 'Errore nel caricamento dei documenti.');
      });
  }, [
    esercizioId,
    currentFolder,
    sortField,
    sortDir,
    debouncedSearch,
    inArchivio,
    presetScadenza,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, rilettura]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  // The row's own restriction travels onto the stack: it is what a form opened
  // inside this folder starts from, and the folder tree that would also say it
  // is read only when the attachment form opens.
  const navigateToFolder = (folder) => {
    setSearchTerm('');
    setDebouncedSearch('');
    setFolderStack((prev) => [
      ...prev,
      {
        id: folder.id,
        titolo: folder.titolo,
        repartiVisibilita: folder.repartiVisibilita || [],
      },
    ]);
  };

  // What the open folder passes down to whatever is created in it — its own
  // restriction, else its parent's — and, as `repartiAmmessi`, the reparti a
  // new element in it can be restricted to: `null` is no limit.
  const restrizioneCorrente = restrizioneEreditata(folderStack);
  const repartiAmmessiCorrenti =
    restrizioneCorrente.length > 0 ? restrizioneCorrente : null;

  const navigateBack = (index) => {
    setSearchTerm('');
    setDebouncedSearch('');
    if (index < 0) {
      setFolderStack([]);
    } else {
      setFolderStack((prev) => prev.slice(0, index + 1));
    }
  };

  /**
   * A preset sorts by expiry, soonest first for what is about to expire and the
   * most recent first for what has expired, unless the operator has picked an
   * order from the menu, whichever it is; the folder open on screen goes, since
   * the flat list has no folders to be in.
   */
  const choosePreset = (valore) => {
    if (valore == null) {
      clearPreset();
      return;
    }
    setPresetScadenza(valore);
    setFolderStack([]);
    if (origineOrdine !== 'operatore') {
      setSortField('scadenza');
      setSortDir(trovaPreset(valore).ordine);
      setOrigineOrdine('preset');
    }
  };

  const clearPreset = () => {
    setPresetScadenza(null);
    if (origineOrdine === 'preset') {
      setSortField('data');
      setSortDir('DESC');
      setOrigineOrdine('predefinito');
    }
  };

  // Each view starts at its root, with no search and no preset: a folder open
  // in one may hold nothing in the other.
  const changeView = (nuova) => {
    if (nuova === vista) return;
    setVista(nuova);
    setFolderStack([]);
    setSearchTerm('');
    setDebouncedSearch('');
    clearPreset();
  };

  const handleRowClick = (item) => {
    if (item.tipo === 'CARTELLA') {
      navigateToFolder(item);
    }
  };

  const openRowMenu = (event, item) => {
    event.stopPropagation();
    setRowMenuAnchor(event.currentTarget);
    setSelectedItem(item);
  };

  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setSelectedItem(null);
  };

  const handleViewDownload = (item) => {
    closeRowMenu();
    getDocumentoFile(item.id)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch((err) => {
        reportError(err.message || 'Errore nel download del file.');
      });
  };

  const openCreateFolderDialog = () => {
    fetchReparti();
    const visibilita = visibilitaDa(restrizioneCorrente);
    setFolderDialog({
      ...FOLDER_DIALOG_CHIUSO,
      open: true,
      cartellaSuperioreId: currentFolder?.id || null,
      visibilita,
      visibilitaIniziale: visibilita,
      repartiAmmessi: repartiAmmessiCorrenti,
    });
  };

  const openEditFolderDialog = (item) => {
    closeRowMenu();
    fetchReparti();
    // The row's own parent: a row reached through a search sits anywhere in the
    // esercizio, and the update route moves the folder under whatever parent it
    // is sent. The folder open on screen is the answer only for a row that
    // does not say.
    const cartellaSuperioreId =
      item.cartellaSuperioreId !== undefined
        ? (item.cartellaSuperioreId ?? null)
        : currentFolder?.id || null;
    const visibilita = visibilitaDa(item.repartiVisibilita);
    setFolderDialog({
      ...FOLDER_DIALOG_CHIUSO,
      open: true,
      mode: 'edit',
      titolo: item.titolo,
      titoloIniziale: item.titolo,
      cartellaId: item.id,
      cartellaSuperioreId,
      visibilita,
      visibilitaIniziale: visibilita,
      repartiAmmessi:
        cartellaSuperioreId === (currentFolder?.id || null)
          ? repartiAmmessiCorrenti
          : null,
    });
  };

  const closeFolderDialog = () => {
    setFolderDialog(FOLDER_DIALOG_CHIUSO);
  };

  const handleSaveFolder = () => {
    if (!isFolderNameValid(folderDialog.titolo)) return;
    if (!isVisibilitaCompleta(folderDialog.visibilita)) return;
    setSaving(true);
    const cartella = {
      titolo: folderDialog.titolo.trim(),
      cartellaSuperioreId: folderDialog.cartellaSuperioreId,
      // Always said, `[]` for the whole structure: on the update route an
      // absent key means «leave it alone», and the form has a value to send —
      // the ids of a reparto no longer present included, so that an edit of
      // the name does not widen who sees the folder.
      repartiVisibilitaIds: idsVisibilita(folderDialog.visibilita),
    };
    const promise =
      folderDialog.mode === 'create'
        ? createCartella(esercizioId, cartella)
        : updateCartella(esercizioId, folderDialog.cartellaId, cartella);
    promise
      .then(() => {
        setSaving(false);
        closeFolderDialog();
        rileggi();
      })
      .catch((err) => {
        setSaving(false);
        reportError(err.message || 'Errore nel salvataggio della cartella.');
      });
  };

  const openCreateDocumentDialog = () => {
    fetchFolderTree();
    fetchReparti();
    forgetTitleConflict();
    setDocumentDialog({
      ...DOCUMENT_DIALOG_CHIUSO,
      open: true,
      cartellaId: currentFolder?.id || null,
      cartellaNome: currentFolder?.titolo || null,
      visibilita: visibilitaDa(restrizioneCorrente),
    });
  };

  const openEditDocumentDialog = (item) => {
    closeRowMenu();
    fetchFolderTree();
    fetchReparti();
    forgetTitleConflict();
    getDocumentoDettaglio(item.id)
      .then((detail) => {
        setDocumentDialog({
          ...DOCUMENT_DIALOG_CHIUSO,
          open: true,
          mode: 'edit',
          titolo: detail.titolo || '',
          note: detail.note || '',
          documentoId: item.id,
          // The local day, not the UTC one: the app saves an expiry as local
          // midnight, so the UTC day is a day early, and saving the form back
          // would move the expiry that day earlier.
          dataScadenza: formattaGiornoIso(detail.dataScadenza) || '',
          cartellaId: detail.cartellaId || null,
          cartellaNome: detail.cartellaNome || null,
          visibilita: visibilitaDa(detail.repartiVisibilita),
          // The document's own restriction counts as chosen: picking another
          // folder in this form moves the document and must not rewrite who
          // sees it, the way a drag onto a folder does not.
          visibilitaToccata: true,
        });
      })
      .catch((err) => {
        reportError(err.message || 'Errore nel caricamento dei dettagli.');
      });
  };

  /**
   * What both writes that can hit a duplicate title do with a refusal. The
   * confirmation is offered only on the attempt that had not asked to overwrite
   * yet: a refusal on the second one goes to the error dialog like any other, so
   * the confirmation cannot come back forever on a title the server keeps
   * refusing.
   */
  const handleWriteRefusal = (err, { overwrite, pending, fallbackMessage }) => {
    const conflict = overwrite ? null : duplicateTitleConflict(err);
    if (conflict) {
      setTitleConflict({ ...conflict, pending });
      // Only the form has a field to carry the reason after the confirmation is
      // dismissed; a move has no open form at all.
      if (pending.kind === 'form') setTakenTitle(pending.titolo);
      return;
    }

    // The confirmation comes down before the message: a window left standing
    // over a write that will not happen says the opposite of what happened.
    setTitleConflict(null);
    reportError(err.message || fallbackMessage);
  };

  /**
   * The duplicate-title warning is scoped to one folder — «esiste già un
   * allegato con questo titolo in questa cartella» — so moving the attachment
   * elsewhere makes it a claim about a folder the server never checked.
   */
  const selectFolder = (cartellaId, cartellaNome, restrizioneCartella = []) => {
    setTakenTitle(null);
    setDocumentDialog((prev) => ({
      ...prev,
      cartellaId,
      cartellaNome,
      // The folder's restriction is the default for what lands in it, so a
      // visibility the operator has not touched follows the folder.
      visibilita: prev.visibilitaToccata
        ? prev.visibilita
        : visibilitaDa(restrizioneCartella),
    }));
  };

  const forgetTitleConflict = () => {
    setTitleConflict(null);
    setTakenTitle(null);
  };

  const closeDocumentDialog = () => {
    forgetTitleConflict();
    setDocumentDialog(DOCUMENT_DIALOG_CHIUSO);
  };

  // The folder picker's rows, and the reparti the attachment can be restricted
  // to: those that see the folder it is going into. Until the tree arrives,
  // the folder open on screen is the one whose restriction is known.
  const cartelleAppiattite = flattenFolderTree(folderTree);
  const restrizioneCartellaScelta = (() => {
    if (documentDialog.cartellaId == null) return [];
    const scelta = cartelleAppiattite.find(
      (cartella) => cartella.id === documentDialog.cartellaId,
    );
    if (scelta) return scelta.restrizione;
    return documentDialog.cartellaId === currentFolder?.id
      ? restrizioneCorrente
      : [];
  })();
  const repartiAmmessiAllegato =
    restrizioneCartellaScelta.length > 0 ? restrizioneCartellaScelta : null;
  const repartiEsistenti = reparti.stato === 'pronto' ? reparti.lista : null;
  // Complete, and with no reparto the destination folder hides from: reading
  // inherits the folder's restriction, so such a document would be visible to
  // nobody but the capo. The field flags the reparto and says what to do.
  const visibilitaSalvabile =
    isVisibilitaCompleta(documentDialog.visibilita) &&
    isVisibilitaAmmessa(
      documentDialog.visibilita,
      repartiAmmessiAllegato,
      repartiEsistenti,
    );

  /**
   * `overwrite` is not a default: it is the operator's answer to the
   * confirmation, and its absence means "refuse if the title is taken". It
   * arrives as a parameter rather than from state because the second attempt
   * starts from inside the confirmation, before a `setState` would be visible
   * here.
   */
  const saveDocument = (overwrite = false) => {
    if (!documentDialog.titolo.trim()) return;
    if (documentDialog.titolo.trim().length > ALLEGATO_TITOLO_MAX_LENGTH)
      return;
    if (documentDialog.mode === 'create' && !documentDialog.file) return;
    if (!visibilitaSalvabile) return;

    setSaving(true);
    const formData = new FormData();
    formData.append('titolo', documentDialog.titolo.trim());
    formData.append('note', documentDialog.note || '');
    formData.append('esercizioId', esercizioId);
    formData.append('data', new Date().toISOString());
    if (documentDialog.file) {
      formData.append('file', documentDialog.file);
    }
    if (documentDialog.cartellaId) {
      formData.append('cartellaId', documentDialog.cartellaId);
    }
    // A `YYYY-MM-DD` string parses as UTC midnight, which in Rome is still the
    // day the operator picked: the day the server reads the expiry on.
    if (documentDialog.dataScadenza) {
      formData.append(
        'dataScadenza',
        new Date(documentDialog.dataScadenza).toISOString(),
      );
    }
    if (overwrite) {
      formData.append('overwrite', 'true');
    }
    // Always said: on the update route an absent field means «leave it alone»,
    // and the form has a value to send. A multipart field cannot carry an empty
    // list by being left out, so the whole structure travels as one empty
    // value, which the server binds to a list with no elements; several
    // reparti travel as the field repeated, one id each.
    const repartiVisibilitaIds = idsVisibilita(documentDialog.visibilita);
    if (repartiVisibilitaIds.length === 0) {
      formData.append('repartiVisibilitaIds', '');
    } else {
      repartiVisibilitaIds.forEach((id) =>
        formData.append('repartiVisibilitaIds', String(id)),
      );
    }

    const promise =
      documentDialog.mode === 'create'
        ? uploadDocumentoEsercizio(formData)
        : updateDocumentoEsercizio(documentDialog.documentoId, formData);

    promise
      .then(() => {
        setSaving(false);
        forgetTitleConflict();
        closeDocumentDialog();
        rileggi();
      })
      .catch((err) => {
        setSaving(false);
        handleWriteRefusal(err, {
          overwrite,
          pending: { kind: 'form', titolo: documentDialog.titolo.trim() },
          fallbackMessage: 'Errore nel salvataggio del documento.',
        });
      });
  };

  const handleSaveDocument = () => saveDocument(false);

  const openDeleteDialog = (item) => {
    closeRowMenu();
    setDeleteDialog({ open: true, item });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, item: null });
  };

  const handleDelete = () => {
    if (!deleteDialog.item) return;
    setSaving(true);
    const item = deleteDialog.item;
    const promise =
      item.tipo === 'CARTELLA'
        ? deleteCartella(esercizioId, item.id)
        : deleteDocumentoEsercizio(item.id);

    promise
      .then(() => {
        setSaving(false);
        closeDeleteDialog();
        rileggi();
      })
      .catch((err) => {
        setSaving(false);
        reportError(err.message || 'Errore nella cancellazione.');
      });
  };

  const openArchiviaDialog = (item) => {
    closeRowMenu();
    setArchiviaDialog({ open: true, item });
  };

  const closeArchiviaDialog = () => setArchiviaDialog(ARCHIVIA_DIALOG_CHIUSO);

  /**
   * Archiving and restoring are the same write with the flag the other way.
   * Either way the document leaves the view it is in, so the list is read
   * again, and the notice says where it went.
   */
  const writeArchiviazione = (item, archiviato) => {
    setSaving(true);
    archiviaDocumento(item.id, archiviato)
      .then(() => {
        setSaving(false);
        closeArchiviaDialog();
        avvisa(
          archiviato
            ? 'Documento archiviato: lo trovi in Archivio.'
            : 'Documento ripristinato: lo trovi tra gli Allegati.',
          'success',
        );
        rileggi();
      })
      .catch((err) => {
        setSaving(false);
        closeArchiviaDialog();
        reportError(
          err.message ||
            (archiviato
              ? "Errore nell'archiviazione del documento."
              : 'Errore nel ripristino del documento.'),
        );
        // The document is gone: the row still on screen would offer it again.
        if (err.status === 404) rileggi();
      });
  };

  const handleArchivia = () => {
    if (!archiviaDialog.item) return;
    writeArchiviazione(archiviaDialog.item, true);
  };

  // No confirmation: restoring loses nothing, and archiving again is one click.
  const handleRipristina = (item) => writeArchiviazione(item, false);

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      setDocumentDialog((prev) => ({ ...prev, file: files[0] }));
    }
  };

  const handleRowDragStart = (e, item) => {
    if (item.tipo === 'CARTELLA') {
      e.preventDefault();
      return;
    }
    draggedItemRef.current = item;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(item.id));
  };

  const handleFolderDragOver = (e, folder) => {
    if (!draggedItemRef.current) return;
    if (folder.tipo !== 'CARTELLA') return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolderId(folder.id);
  };

  const handleFolderDragLeave = () => {
    setDragOverFolderId(null);
  };

  /**
   * An `Instant` for the server, or `null` for a value it cannot be built from.
   * `toISOString()` throws on an unparseable date, and inside the move that
   * would reach the operator as «Invalid time value».
   */
  const comeIstante = (valore) => {
    if (!valore) return null;
    const data = new Date(valore);
    return Number.isNaN(data.getTime()) ? null : data.toISOString();
  };

  /**
   * Moving an attachment can take a title that is free where it sits and taken
   * where it lands, so the move answers a duplicate title the same way the form
   * does. The ticket asks for the check on «la rinomina/spostamento», and a drag
   * is the only move this screen has.
   *
   * The route replaces the whole record — `updateDocumentoSegnalazioniEsercizio`
   * applies `note` and `dataScadenza` whatever they are — so a move has to carry
   * the fields it is not changing or it wipes them. `dettaglio` is that state,
   * read once before the first attempt and carried on the pending move, so
   * confirming an overwrite repeats the same payload instead of reading it again.
   * `repartoId` is the exception and stays out: the server leaves it alone when
   * it is absent.
   */
  const moveDocumentToFolder = ({
    item,
    cartellaId,
    dettaglio,
    overwrite = false,
  }) => {
    setSaving(true);
    const formData = new FormData();
    formData.append('titolo', item.titolo);
    formData.append('esercizioId', esercizioId);
    formData.append('data', item.data || new Date().toISOString());
    formData.append('cartellaId', cartellaId);
    formData.append('note', dettaglio?.note || '');
    // The detail alone, never the row: a row loaded before someone cleared the
    // expiry would put it back, which is the same kind of loss as the wiped note
    // with the sign reversed. The read is mandatory and the move stops without
    // it, so there is nothing to fall back to.
    const dataScadenza = comeIstante(dettaglio?.dataScadenza);
    if (dataScadenza) {
      formData.append('dataScadenza', dataScadenza);
    }
    if (overwrite) {
      formData.append('overwrite', 'true');
    }

    updateDocumentoEsercizio(item.id, formData)
      .then(() => {
        setSaving(false);
        setTitleConflict(null);
        rileggi();
      })
      .catch((err) => {
        setSaving(false);
        handleWriteRefusal(err, {
          overwrite,
          pending: { kind: 'move', item, cartellaId, dettaglio },
          fallbackMessage: 'Errore nello spostamento del documento.',
        });
      });
  };

  const handleFolderDrop = (e, folder) => {
    e.preventDefault();
    setDragOverFolderId(null);
    const item = draggedItemRef.current;
    draggedItemRef.current = null;
    if (!item || item.tipo === 'CARTELLA') return;

    // One write at a time. `saving` and the pending conflict are single slots, so
    // two moves in the air can end with the second refusal replacing the first
    // one's confirmation: the operator answers one, and the other move never
    // happens with nothing on screen to say so. The write in the air may be a
    // restore, which has no window over the table, so the notice names no kind.
    if (saving) {
      avvisa("Attendi il completamento dell'operazione in corso.");
      return;
    }

    // The note is not on the row, and the move must not send it empty, so the
    // record is read before it is written. A failed read stops the move: moving
    // with what the row happens to hold is what emptied the note.
    setSaving(true);
    getDocumentoDettaglio(item.id)
      .then((dettaglio) =>
        moveDocumentToFolder({ item, cartellaId: folder.id, dettaglio }),
      )
      .catch((err) => {
        setSaving(false);
        reportError(err.message || 'Errore nello spostamento del documento.');
      });
  };

  /**
   * The confirmation stays up while the retry is in flight — that is what its
   * spinner and disabled buttons are for — and comes down when the write settles.
   */
  const handleConflictOverwrite = () => {
    const pending = titleConflict?.pending;
    if (!pending) return;

    if (pending.kind === 'move') {
      moveDocumentToFolder({
        item: pending.item,
        cartellaId: pending.cartellaId,
        dettaglio: pending.dettaglio,
        overwrite: true,
      });
      return;
    }
    saveDocument(true);
  };

  // A refused move is called off, not renamed: the document stays in the folder
  // it was in, which is a state the operator asked for a moment ago. Renaming is
  // the way out of the form, where the title is the thing being typed.
  const handleConflictDismiss = () => setTitleConflict(null);

  const handleRowDragEnd = () => {
    draggedItemRef.current = null;
    setDragOverFolderId(null);
  };

  // The menu opens with the order on screen highlighted and focused, so a click
  // or Enter on that entry is how it gets closed: nothing the operator can see
  // changes, and it is not their choice of an order — a preset chosen next
  // still sets its own, and one taken off still undoes its own.
  const handleSortSelect = (field, dir) => {
    setSortMenuAnchor(null);
    if (field === sortField && dir === sortDir) return;
    setSortField(field);
    setSortDir(dir);
    setOrigineOrdine('operatore');
  };

  const folderCount = data.filter((d) => d.tipo === 'CARTELLA').length;
  const docCount = data.filter((d) => d.tipo !== 'CARTELLA').length;

  // Blocks the save: a title longer than the maximum is refused by the server
  // anyway.
  const documentoTitoloError =
    documentDialog.titolo.trim().length > ALLEGATO_TITOLO_MAX_LENGTH
      ? `Il titolo non può superare i ${ALLEGATO_TITOLO_MAX_LENGTH} caratteri.`
      : '';

  // A duplicate title does not disable Salva: overwriting is one of the two ways
  // out, and disabling it would remove the very road the confirmation offers.
  // Only the length rule, above, is in the button's `disabled`.
  // Still the refused title, so the warning stands; a different one has never
  // been refused, and the server is the only thing that could say otherwise.
  const titleIsTaken =
    !!takenTitle && documentDialog.titolo.trim() === takenTitle;

  const sortLabel = (
    ORDINAMENTI.find((opt) => opt.field === sortField && opt.dir === sortDir) ||
    ORDINAMENTI[0]
  ).breve;

  // With a preset the rows are documents from anywhere in the tree, so each
  // says where it sits.
  const piatta = !!presetScadenza;

  const vuoto = (() => {
    // Not one of the empty answers below: those are claims about the documents,
    // and after a failed read there is nothing to make a claim from.
    if (letturaFallita) {
      return {
        Icona: ErrorOutlineIcon,
        titolo: "Impossibile caricare l'elenco",
        testo: 'Non è stato possibile leggere i documenti. Riprova.',
        riprova: true,
      };
    }
    if (debouncedSearch) {
      return {
        Icona: SearchIcon,
        titolo: 'Nessun risultato',
        testo: `Nessun documento trovato per "${debouncedSearch}". Prova con un altro termine.`,
      };
    }
    if (piatta) {
      return {
        Icona: EventIcon,
        titolo: 'Nessun documento',
        testo: trovaPreset(presetScadenza).vuoto,
      };
    }
    if (inArchivio) {
      return {
        Icona: ArchiveIcon,
        titolo: 'Nessun documento archiviato',
        testo: currentFolder
          ? 'Questa cartella non contiene più documenti archiviati.'
          : 'Qui trovi i documenti archiviati. Per archiviarne uno, scegli «Archivia» dal menu della sua riga in Allegati.',
      };
    }
    return {
      Icona: FolderOpenIcon,
      titolo: 'Nessun contenuto',
      testo:
        'Inizia aggiungendo un allegato o creando una cartella per organizzare i tuoi documenti.',
    };
  })();

  const RootIcon = inArchivio ? ArchiveIcon : HomeIcon;

  return (
    <div className={classes.root}>
      {/* ─── Header ─── */}
      <div className={classes.headerSection}>
        <VistaDocumentiToggle vista={vista} onChange={changeView} />
        {/* At the root the trail would only repeat the title below it: it
            shows once a folder is open, to climb back out of it. */}
        {folderStack.length > 0 && (
          <Breadcrumbs
            aria-label="Percorso della cartella"
            className={classes.breadcrumbs}
            separator={<ChevronRightIcon style={{ fontSize: 18 }} />}
          >
            <Link
              component="button"
              onClick={() => navigateBack(-1)}
              className={`${classes.breadcrumbLink} ${
                folderStack.length === 0 ? classes.breadcrumbCurrent : ''
              }`}
              color={folderStack.length === 0 ? 'textPrimary' : 'inherit'}
              underline="none"
            >
              <RootIcon style={{ fontSize: 18 }} />
              {inArchivio ? 'Archivio' : 'Allegati'}
            </Link>
            {folderStack.map((f, idx) => (
              <Tooltip key={f.id} title={f.titolo}>
                <Link
                  component="button"
                  onClick={() => navigateBack(idx)}
                  className={`${classes.breadcrumbLink} ${
                    idx === folderStack.length - 1
                      ? classes.breadcrumbCurrent
                      : ''
                  }`}
                  color={
                    idx === folderStack.length - 1 ? 'textPrimary' : 'inherit'
                  }
                  underline="none"
                >
                  <FolderIcon style={{ fontSize: 16 }} />
                  <span className={classes.breadcrumbText}>{f.titolo}</span>
                </Link>
              </Tooltip>
            ))}
          </Breadcrumbs>
        )}

        <Tooltip title={currentFolder ? currentFolder.titolo : ''}>
          <Typography className={classes.pageTitle}>
            {currentFolder
              ? currentFolder.titolo
              : inArchivio
                ? 'Archivio'
                : 'Documenti e Allegati'}
          </Typography>
        </Tooltip>
        <Typography className={classes.pageSubtitle}>
          {(() => {
            if (inArchivio) {
              return currentFolder
                ? 'Documenti archiviati nella cartella'
                : "Documenti archiviati dell'esercizio: ripristinali per riportarli tra gli Allegati";
            }
            return currentFolder
              ? 'Contenuto della cartella'
              : "Gestisci documenti e cartelle dell'esercizio";
          })()}
        </Typography>
      </div>

      {/* ─── Toolbar ─── */}
      <div className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder="Cerca documenti..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 20, color: '#9CA3AF' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    style={{ padding: 4 }}
                  >
                    <CloseIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Tooltip title={`Ordine: ${sortLabel}`}>
            <IconButton
              className={`${classes.sortButton} ${
                sortField !== 'data' || sortDir !== 'DESC'
                  ? classes.sortButtonActive
                  : ''
              }`}
              onClick={(e) => setSortMenuAnchor(e.currentTarget)}
              size="small"
            >
              <SortIcon style={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={sortMenuAnchor}
            open={Boolean(sortMenuAnchor)}
            onClose={() => setSortMenuAnchor(null)}
            className={classes.sortMenu}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {ORDINAMENTI.map((opt) => (
              <MenuItem
                key={`${opt.field}-${opt.dir}`}
                className={`${classes.sortMenuItem} ${
                  sortField === opt.field && sortDir === opt.dir
                    ? classes.sortMenuItemActive
                    : ''
                }`}
                onClick={() => handleSortSelect(opt.field, opt.dir)}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Menu>
        </div>
        {/* What is created is active, so it would not show in Archivio, the
            very view it was created from. */}
        {!inArchivio && (
          <div className={classes.toolbarRight}>
            <PromemoriaScadenzeButton
              esercizioId={esercizioId}
              promemoria={promemoria}
              onAvviso={avvisa}
              className={`${classes.actionButton} ${classes.outlinedAction}`}
            />
            <Tooltip
              title={
                canCreateSubfolder
                  ? 'Crea una nuova cartella'
                  : 'Limite di profondità raggiunto'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CreateNewFolderIcon />}
                  onClick={openCreateFolderDialog}
                  disabled={!canCreateSubfolder}
                  className={`${classes.actionButton} ${classes.outlinedAction}`}
                >
                  Nuova cartella
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openCreateDocumentDialog}
              className={`${classes.actionButton} ${classes.primaryAction}`}
              style={{ color: theme.palette.common.white }}
            >
              Nuovo allegato
            </Button>
          </div>
        )}
      </div>

      {!inArchivio && (
        <RiepilogoPromemoria configurazione={promemoria.configurazione} />
      )}

      {/* An expiry is a question about the documents in use: archived ones
          are left out of every preset by the server, so Archivio has none. */}
      {!inArchivio && (
        <PresetScadenzaChips
          valore={presetScadenza}
          conteggio={loading || letturaFallita ? null : docCount}
          onChange={choosePreset}
        />
      )}

      {/* ─── Stats bar ─── */}
      {!loading && data.length > 0 && (
        <div className={classes.statsBar}>
          {folderCount > 0 && (
            <div className={classes.statItem}>
              <div
                className={classes.statIcon}
                style={{ backgroundColor: iconColors.folder.bg }}
              >
                <FolderIcon
                  style={{ fontSize: 18, color: iconColors.folder.fg }}
                />
              </div>
              <div>
                <Typography className={classes.statValue}>
                  {folderCount}
                </Typography>
                <Typography className={classes.statLabel}>
                  {folderCount === 1 ? 'cartella' : 'cartelle'}
                </Typography>
              </div>
            </div>
          )}
          {docCount > 0 && (
            <div className={classes.statItem}>
              <div
                className={classes.statIcon}
                style={{ backgroundColor: iconColors.generic.bg }}
              >
                <GenericFileIcon
                  style={{ fontSize: 18, color: iconColors.generic.fg }}
                />
              </div>
              <div>
                <Typography className={classes.statValue}>
                  {docCount}
                </Typography>
                <Typography className={classes.statLabel}>
                  {docCount === 1 ? 'documento' : 'documenti'}
                </Typography>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Content ─── */}
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress
            size={36}
            style={{ color: theme.palette.primary.main }}
          />
          <Typography variant="body2" color="textSecondary">
            Caricamento...
          </Typography>
        </div>
      ) : data.length === 0 ? (
        <div className={classes.emptyState}>
          <div className={classes.emptyIcon}>
            <vuoto.Icona
              style={{ fontSize: 36, color: theme.palette.primary.main }}
            />
          </div>
          <Typography className={classes.emptyTitle}>{vuoto.titolo}</Typography>
          <Typography className={classes.emptySubtitle}>
            {vuoto.testo}
          </Typography>
          {vuoto.riprova && (
            <Button
              variant="outlined"
              color="primary"
              onClick={rileggi}
              className={`${classes.actionButton} ${classes.emptyAction}`}
            >
              Riprova
            </Button>
          )}
        </div>
      ) : (
        <TableContainer
          className={classes.tableWrapper}
          component={Paper}
          elevation={0}
        >
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell
                  className={`${classes.iconCell} ${classes.tableHeadCell}`}
                />
                <TableCell className={classes.tableHeadCell}>Nome</TableCell>
                {piatta && (
                  <TableCell className={classes.tableHeadCell}>
                    Cartella
                  </TableCell>
                )}
                <TableCell className={classes.tableHeadCell}>Data</TableCell>
                <TableCell className={classes.tableHeadCell}>
                  Scadenza
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  Visibilità
                </TableCell>
                <TableCell
                  className={`${classes.actionsCell} ${
                    inArchivio ? classes.restoreCell : ''
                  } ${classes.tableHeadCell}`}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item) => {
                const isFolder = item.tipo === 'CARTELLA';
                // An archived document is frozen: it is moved, like it is
                // edited, once restored to Allegati.
                const isDraggable = !isFolder && !inArchivio;
                const isDropTarget = isFolder && !inArchivio;
                const isDragTarget =
                  isDropTarget && dragOverFolderId === item.id;

                return (
                  <TableRow
                    key={`${item.tipo}-${item.id}`}
                    className={`${classes.tableRow} ${
                      isFolder ? classes.folderRow : ''
                    } ${isDraggable ? classes.draggableRow : ''} ${
                      isDragTarget ? classes.folderRowDragOver : ''
                    }`}
                    onClick={() => handleRowClick(item)}
                    draggable={isDraggable}
                    onDragStart={
                      isDraggable
                        ? (e) => handleRowDragStart(e, item)
                        : undefined
                    }
                    onDragEnd={isDraggable ? handleRowDragEnd : undefined}
                    onDragOver={
                      isDropTarget
                        ? (e) => handleFolderDragOver(e, item)
                        : undefined
                    }
                    onDragLeave={
                      isDropTarget ? handleFolderDragLeave : undefined
                    }
                    onDrop={
                      isDropTarget
                        ? (e) => handleFolderDrop(e, item)
                        : undefined
                    }
                  >
                    <TableCell className={classes.iconCell}>
                      {getFileIcon(item, classes.iconBadge)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {isFolder ? (
                        <span className={classes.folderTitle}>
                          <Tooltip title={item.titolo}>
                            <span className={classes.titleText}>
                              {item.titolo}
                            </span>
                          </Tooltip>
                          <ArrowIcon className={classes.folderArrow} />
                        </span>
                      ) : (
                        <>
                          <Tooltip title={item.titolo}>
                            <span
                              className={`${classes.titleCell} ${classes.titleText}`}
                            >
                              {item.titolo}
                            </span>
                          </Tooltip>
                          <ArchiviatoBadge
                            dataArchiviazione={item.dataArchiviazione}
                          />
                        </>
                      )}
                    </TableCell>
                    {piatta && (
                      <TableCell
                        className={`${classes.tableCell} ${classes.dateCell}`}
                      >
                        {descriviPercorso(item.percorsoCartella)}
                      </TableCell>
                    )}
                    <TableCell
                      className={`${classes.tableCell} ${classes.dateCell}`}
                    >
                      {formatDate(item.data)}
                    </TableCell>
                    <TableCell
                      className={`${classes.tableCell} ${classes.dateCell}`}
                    >
                      {/* An archived document is out of the presets and of
                          the expiry notifications, so its expiry is a plain
                          date: a red «Scaduto» would ask for an action nobody
                          has to take. */}
                      {item.dataArchiviazione != null ? (
                        formatDate(item.dataScadenza)
                      ) : (
                        <ScadenzaPill dataScadenza={item.dataScadenza}>
                          {formatDate(item.dataScadenza)}
                        </ScadenzaPill>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {/* Search and preset results come from anywhere in the
                          esercizio, so only the rows of the open folder can be
                          read against what it passes down. */}
                      <VisibilitaPills
                        repartiVisibilita={item.repartiVisibilita}
                        ereditata={
                          debouncedSearch || piatta ? null : restrizioneCorrente
                        }
                        repartiEsistenti={repartiEsistenti}
                      />
                    </TableCell>
                    <TableCell
                      className={`${classes.actionsCell} ${
                        inArchivio ? classes.restoreCell : ''
                      }`}
                    >
                      {/* Archivio is read-only: a document there can only go
                          back to Allegati, where it is opened, edited, moved
                          or deleted. Its folders are only walked through. */}
                      {!inArchivio && (
                        <IconButton
                          size="small"
                          aria-label={`Azioni su ${item.titolo}`}
                          onClick={(e) => openRowMenu(e, item)}
                          className={classes.moreButton}
                        >
                          <MoreVertIcon style={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                      {inArchivio && !isFolder && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<UnarchiveIcon />}
                          aria-label={`Ripristina ${item.titolo}`}
                          disabled={saving}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRipristina(item);
                          }}
                          className={classes.restoreButton}
                        >
                          Ripristina
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ─── Row context menu ─── */}
      <Menu
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={closeRowMenu}
        className={classes.contextMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {selectedItem && selectedItem.tipo !== 'CARTELLA' && (
          <MenuItem
            onClick={() => handleViewDownload(selectedItem)}
            className={classes.contextMenuItem}
          >
            <MuiListItemIcon style={{ minWidth: 36 }}>
              <VisibilityIcon style={{ fontSize: 18, color: '#6B7280' }} />
            </MuiListItemIcon>
            <MuiListItemText
              primary="Visualizza / Scarica"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
        {selectedItem && selectedItem.tipo !== 'CARTELLA' && (
          <MenuItem
            onClick={() => openEditDocumentDialog(selectedItem)}
            className={classes.contextMenuItem}
          >
            <MuiListItemIcon style={{ minWidth: 36 }}>
              <EditIcon style={{ fontSize: 18, color: '#6B7280' }} />
            </MuiListItemIcon>
            <MuiListItemText
              primary="Modifica"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
        {selectedItem &&
          selectedItem.tipo !== 'CARTELLA' &&
          selectedItem.dataArchiviazione == null && (
            <MenuItem
              onClick={() => openArchiviaDialog(selectedItem)}
              disabled={saving}
              className={classes.contextMenuItem}
            >
              <MuiListItemIcon style={{ minWidth: 36 }}>
                <ArchiveIcon
                  style={{ fontSize: 18, color: theme.palette.text.secondary }}
                />
              </MuiListItemIcon>
              <MuiListItemText
                primary="Archivia"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </MenuItem>
          )}
        {selectedItem && selectedItem.tipo === 'CARTELLA' && (
          <MenuItem
            onClick={() => openEditFolderDialog(selectedItem)}
            className={classes.contextMenuItem}
          >
            <MuiListItemIcon style={{ minWidth: 36 }}>
              <EditIcon style={{ fontSize: 18, color: '#6B7280' }} />
            </MuiListItemIcon>
            <MuiListItemText
              primary="Modifica"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
        {selectedItem && (
          <MenuItem
            onClick={() => openDeleteDialog(selectedItem)}
            className={`${classes.contextMenuItem} ${classes.contextMenuDanger}`}
          >
            <MuiListItemIcon style={{ minWidth: 36 }}>
              <DeleteIcon style={{ fontSize: 18, color: '#EF4444' }} />
            </MuiListItemIcon>
            <MuiListItemText
              primary="Elimina"
              primaryTypographyProps={{
                variant: 'body2',
                style: { color: '#EF4444' },
              }}
            />
          </MenuItem>
        )}
      </Menu>

      <FolderDialog
        open={folderDialog.open}
        mode={folderDialog.mode}
        titolo={folderDialog.titolo}
        titoloIniziale={folderDialog.titoloIniziale}
        visibilita={folderDialog.visibilita}
        visibilitaIniziale={folderDialog.visibilitaIniziale}
        reparti={reparti}
        repartiAmmessi={folderDialog.repartiAmmessi}
        saving={saving}
        onClose={closeFolderDialog}
        onTitoloChange={(titolo) =>
          setFolderDialog((prev) => ({ ...prev, titolo }))
        }
        onVisibilitaChange={(visibilita) =>
          setFolderDialog((prev) => ({ ...prev, visibilita }))
        }
        onSave={handleSaveFolder}
      />

      {/* ─── Document create/edit dialog ─── */}
      <Dialog
        open={documentDialog.open}
        onClose={closeDocumentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.dialogTitle}>
          <div
            className={classes.dialogTitleIcon}
            style={{
              backgroundColor:
                documentDialog.mode === 'create'
                  ? fade(theme.palette.primary.main, 0.1)
                  : iconColors.generic.bg,
            }}
          >
            {documentDialog.mode === 'create' ? (
              <NoteAddIcon style={{ color: theme.palette.primary.main }} />
            ) : (
              <EditIcon style={{ color: iconColors.generic.fg }} />
            )}
          </div>
          <Typography className={classes.dialogTitleText}>
            {documentDialog.mode === 'create'
              ? 'Nuovo allegato'
              : 'Modifica allegato'}
          </Typography>
        </div>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.dialogForm}>
            <TextField
              // With an `id` MUI ties the label to the field and the helper
              // text to `aria-describedby`: without one, the duplicate-title
              // warning reaches only whoever is looking at the screen.
              id="documenti-titolo"
              label="Titolo"
              variant="outlined"
              fullWidth
              autoFocus
              className={classes.dialogInput}
              value={documentDialog.titolo}
              onChange={(e) => {
                const val = e.target.value;
                setDocumentDialog((prev) => ({
                  ...prev,
                  titolo: val,
                }));
              }}
              // The two messages this field can carry are not the same kind of
              // message, and they never apply at once: `takenTitle` only holds a
              // title the length rule already let through, so a title over the
              // maximum is by then a different title and `titleIsTaken` is false.
              // Over the maximum the server refuses whatever the operator does
              // next, so `error` paints the field red and Salva goes grey; a taken
              // title refuses nothing — Salva stays live and confirming the
              // overwrite goes through — so it gets the warning surface instead.
              // The warning travels in the same helper slot rather than beside the
              // field so that MUI keeps it in `aria-describedby`; outside it, it
              // would reach only whoever is looking at the screen.
              error={!!documentoTitoloError}
              helperText={
                documentoTitoloError ||
                (titleIsTaken ? (
                  <WarningBanner testId="avviso-titolo-duplicato">
                    {DUPLICATE_TITLE_MESSAGE}
                  </WarningBanner>
                ) : null)
              }
              // A block inside the helper text: `<p>` is what FormHelperText
              // renders by default, and a `<div>` inside one is invalid markup
              // the browser reopens somewhere else. The class only undoes the
              // slot's own inset, which would hold the band off the edges of the
              // field it belongs to.
              FormHelperTextProps={
                titleIsTaken
                  ? {
                      component: 'div',
                      className: classes.avvisoTitoloHelper,
                    }
                  : undefined
              }
              placeholder="Es. Certificazione sanitaria"
            />
            <TextField
              label="Note"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              className={classes.dialogInput}
              value={documentDialog.note}
              onChange={(e) => {
                const val = e.target.value;
                setDocumentDialog((prev) => ({
                  ...prev,
                  note: val,
                }));
              }}
              placeholder="Aggiungi una descrizione opzionale..."
            />
            <TextField
              id="documenti-data-scadenza"
              label="Data scadenza"
              type="date"
              variant="outlined"
              fullWidth
              className={classes.dialogInput}
              InputLabelProps={{ shrink: true }}
              value={documentDialog.dataScadenza}
              onChange={(e) => {
                const val = e.target.value;
                setDocumentDialog((prev) => ({
                  ...prev,
                  dataScadenza: val,
                }));
              }}
            />

            {/* Folder picker */}
            <div className={classes.folderPickerSection}>
              <Typography className={classes.folderPickerLabel}>
                Cartella di destinazione
              </Typography>
              {folderTreeLoading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <div className={classes.folderPickerContainer}>
                  <List dense disablePadding>
                    <ListItem
                      button
                      className={`${classes.folderPickerItem} ${
                        documentDialog.cartellaId === null
                          ? classes.folderPickerItemSelected
                          : ''
                      }`}
                      onClick={() => selectFolder(null, null, [])}
                    >
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <HomeIcon
                          fontSize="small"
                          style={{
                            color:
                              documentDialog.cartellaId === null
                                ? theme.palette.primary.main
                                : '#9CA3AF',
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Root (nessuna cartella)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          style: {
                            fontWeight:
                              documentDialog.cartellaId === null ? 600 : 400,
                          },
                        }}
                      />
                    </ListItem>
                    {cartelleAppiattite.map((folder) => (
                      <ListItem
                        key={folder.id}
                        button
                        className={`${classes.folderPickerItem} ${
                          documentDialog.cartellaId === folder.id
                            ? classes.folderPickerItemSelected
                            : ''
                        }`}
                        style={{ paddingLeft: 12 + folder.level * 24 }}
                        onClick={() =>
                          selectFolder(
                            folder.id,
                            folder.nome,
                            folder.restrizione,
                          )
                        }
                      >
                        <ListItemIcon style={{ minWidth: 32 }}>
                          {documentDialog.cartellaId === folder.id ? (
                            <FolderOpenIcon
                              fontSize="small"
                              style={{ color: iconColors.folder.fg }}
                            />
                          ) : (
                            <FolderIcon
                              fontSize="small"
                              style={{ color: '#F9A825' }}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={folder.nome}
                          primaryTypographyProps={{
                            variant: 'body2',
                            noWrap: true,
                            title: folder.nome,
                            style: {
                              fontWeight:
                                documentDialog.cartellaId === folder.id
                                  ? 600
                                  : 400,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </div>
              )}
            </div>

            <VisibilitaField
              id="allegato-visibilita"
              value={documentDialog.visibilita}
              onChange={(visibilita) =>
                setDocumentDialog((prev) => ({
                  ...prev,
                  visibilita,
                  visibilitaToccata: true,
                }))
              }
              reparti={reparti}
              repartiAmmessi={repartiAmmessiAllegato}
              disabled={saving}
            />

            {/* File drop zone */}
            <div
              className={`${classes.fileDropZone} ${
                documentDialog.file ? classes.fileDropZoneActive : ''
              }`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleFileDrop}
              onClick={() =>
                document.getElementById('documenti-file-input').click()
              }
            >
              <div className={classes.dropZoneIcon}>
                <CloudUploadIcon
                  style={{ fontSize: 24, color: theme.palette.primary.main }}
                />
              </div>
              {documentDialog.file ? (
                <>
                  <Typography
                    variant="body2"
                    style={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {documentDialog.file.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginTop: 4 }}
                  >
                    Clicca per sostituire il file
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    Trascina un file qui
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginTop: 4 }}
                  >
                    oppure{' '}
                    <span
                      style={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                      }}
                    >
                      clicca per selezionare
                    </span>
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{ color: '#9CA3AF', marginTop: 8, display: 'block' }}
                  >
                    PDF, JPG, PNG — max 10MB
                  </Typography>
                </>
              )}
            </div>
            <input
              id="documenti-file-input"
              type="file"
              accept="application/pdf,image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const selectedFile =
                  e.target.files && e.target.files.length > 0
                    ? e.target.files[0]
                    : null;
                if (selectedFile) {
                  setDocumentDialog((prev) => ({
                    ...prev,
                    file: selectedFile,
                  }));
                }
              }}
            />
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={closeDocumentDialog}
            disabled={saving}
            className={classes.dialogCancelBtn}
          >
            Annulla
          </Button>
          <Button
            onClick={handleSaveDocument}
            color="primary"
            variant="contained"
            disabled={
              saving ||
              !documentDialog.titolo.trim() ||
              !!documentoTitoloError ||
              (documentDialog.mode === 'create' && !documentDialog.file) ||
              !visibilitaSalvabile
            }
            className={classes.dialogSaveBtn}
            style={{ color: '#fff' }}
          >
            {saving ? (
              <CircularProgress size={20} style={{ color: '#fff' }} />
            ) : documentDialog.mode === 'create' ? (
              'Carica allegato'
            ) : (
              'Salva modifiche'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Title already in use: rename or overwrite ─── */}
      <OverwriteAttachmentDialog
        open={!!titleConflict}
        existingTitle={titleConflict?.existingTitle}
        uploadedDate={titleConflict?.uploadedDate}
        saving={saving}
        dismissLabel={
          titleConflict?.pending?.kind === 'move' ? 'Annulla' : 'Rinomina'
        }
        onDismiss={handleConflictDismiss}
        onOverwrite={handleConflictOverwrite}
      />

      {/* ─── Delete confirmation dialog ─── */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogContent
          style={{ padding: '32px 24px 8px', textAlign: 'center' }}
        >
          <div className={classes.deleteDialogIcon}>
            <WarningIcon style={{ fontSize: 28, color: '#EF4444' }} />
          </div>
          <Typography
            style={{
              ...theme.typography.dialogTitle,
              marginBottom: 8,
            }}
          >
            Conferma eliminazione
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ lineHeight: 1.6, overflowWrap: 'anywhere' }}
          >
            {deleteDialog.item?.tipo === 'CARTELLA'
              ? `Vuoi eliminare la cartella "${deleteDialog.item?.titolo}"? La cartella deve essere vuota per poter essere eliminata: contano anche i documenti archiviati.`
              : `Vuoi eliminare il documento "${deleteDialog.item?.titolo}"? Questa azione è irreversibile.`}
          </Typography>
        </DialogContent>
        <DialogActions
          className={classes.dialogActions}
          style={{ justifyContent: 'center', paddingBottom: 24 }}
        >
          <Button
            onClick={closeDeleteDialog}
            disabled={saving}
            className={classes.dialogCancelBtn}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDelete}
            disabled={saving}
            className={classes.dialogDeleteBtn}
          >
            {saving ? (
              <CircularProgress size={20} style={{ color: '#fff' }} />
            ) : (
              'Elimina'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ArchiviaDocumentoDialog
        open={archiviaDialog.open}
        titolo={archiviaDialog.item?.titolo}
        saving={saving}
        onCancel={closeArchiviaDialog}
        onConfirm={handleArchivia}
      />

      <CustomSnackbar
        key={avviso.seq}
        open={!!avviso.testo}
        message={avviso.testo}
        severity={avviso.severity}
        onClose={() => setAvviso((prev) => ({ ...prev, testo: '' }))}
      />

      {/* Error dialog */}
    </div>
  );
}

DocumentiSection.propTypes = {
  esercizioId: PropTypes.string.isRequired,
};
